/**
 * useHotspotDetail — W1.3 / W4.7
 *
 * Holt Hotspot-Detail vom Backend (GET /api/v2/hotspots/:id).
 * Lokal typisiert bis B1.1 in api-types landet.
 * W4.7: Neue Felder aus B5.7 (alle optional/nullable für Rückwärtskompatibilität).
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import type { components } from '@heliumtracker/api-types'
import type { HotspotStatus, HotspotEra } from './useHotspots'
import type { NetworkShare } from '../dashboard/useDashboard'

/**
 * Hero-Status-Blöcke aus Welle 3 / FE-Story 1.6 — kommen additiv im selben
 * GET /api/v2/hotspots/:id Response (cdr_verification, ban_status,
 * unique_connections). Typen direkt aus den generierten api-types.
 */
export type HotspotHeroStatus = components['schemas']['HotspotHeroStatus']

/**
 * F-036/suite#3: Ein Eintrag aus hotspot.heartbeats[] im Show-Response
 * (GET /api/v2/hotspots/:id). Spec-Shape, max. 50 Eintraege, DESC sortiert.
 */
export type HotspotHeartbeat = {
  timestamp: string
  lat: number | null
  lng: number | null
  distance_to_asserted_m: number | null
  cell_type: string | null
  online_status: boolean
}

// --- Lokale Typen ---

export type HotspotDetail = {
  id: number
  name: string | null
  era: HotspotEra
  device_type: string | null
  status: HotspotStatus
  online_pct: number | null
  hnt_24h: number | null
  hnt_7d: number | null
  hnt_30d: number | null
  lat: number | null
  lng: number | null
  added_at: string
  // NEU aus B5.7:
  address: string | null // Blockchain-Pubkey
  street: string | null // physische Strasse
  /** Städteseiten-Relaunch (09.2026): numerische City.id für den Link auf /cities/:id. */
  city_id?: number | null
  city_name: string | null
  country: string | null
  wallet_address: string | null
  wallet_hotspot_count: number | null
  maker_name: string | null
  elevation: number | null // m
  gain: number | null // dBi
  reward_scale: number | null // 0..1
  reward_scale_pct: number | null
  denylist: boolean
  // hotspot_guard_enabled entfernt — Feature deprecated (Backend + Frontend)
  last_poc_activity_at: string | null
  last_beacon_at: string | null
  last_unselected_at: string | null
  online_status: 'online' | 'offline' | null
  online_status_minutes_ago: number | null
  online_status_timestamp?: string | null
  beacons_sent_5d: number | null
  witnesses_valid_5d: number | null
  witnesses_unselected_5d: number | null
  witnesses_invalid_5d: number | null
  /**
   * Netzwerk-Rang des Hotspots (rank_7d, denormalisiert; 1 = bester).
   * null, solange der stuendliche Rank-Cron noch nicht gelaufen ist oder der
   * Hotspot keine 7-Tage-Rewards hat → UI zeigt "N/A" + graue Krone.
   */
  rank?: number | null
  /** Perzentil des Rangs, 100 = bester. Quelle der Kronenfarbe. */
  rank_percentile?: number | null
  /** Alias zu rank_percentile (v1-Feldname), vom BE mitgeliefert. */
  rank_percentage?: number | null
  /** Grundgesamtheit des Rangs (Anzahl gerankter Hotspots). */
  rank_total?: number | null

  // --- Standortdaten (R4) ---
  /** Antennengewinn in dBi — ANZEIGEN. `gain` ist der Rohwert in Zehntel-dBi. */
  gain_dbi?: number | null
  /** Hoehe in Metern (Alias zu elevation). */
  elevation_m?: number | null
  entity_type?: 'hotspot' | 'validator' | null
  /** Vom BE gebaute Explorer-URL (Hotspot- bzw. Validator-Zweig). */
  explorer_url?: string | null

  // --- DenyList (R4) ---
  denylist_status?: boolean | null
  /** Zeitpunkt der ersten Erfassung auf der DenyList (Import-Zeitpunkt). */
  denylist_since?: string | null
  denylist_info_url?: string | null

  // --- PoC (R4) ---
  /** false fuer MOBILE/WiFi/CBRS und Validatoren → PoC-Karte ausblenden. */
  poc_applicable?: boolean | null
  /** Laenge des PoC-Fensters in Tagen (aktuell 5) — in Texte interpolieren. */
  poc_window_days?: number | null
  /** valid / (valid + unselected + invalid) im PoC-Fenster, 0..1 oder null. */
  poc_success_rate_5d?: number | null
  last_poc_minutes_ago?: number | null
  /** Minuten seit dem letzten Heartbeat (v1: poc_dif). */
  heartbeat_minutes_ago?: number | null

  // --- Hotspot-Guard (R4, v1-Pro-Hotspot-Schalter) ---
  /** Pro-Hotspot-Schalter. NICHT identisch mit guard_status (Evaluator-Ergebnis). */
  guard_enabled?: boolean | null
  guard_muted_until?: string | null
  guard_muted?: boolean | null
  /** false fuer Free-Accounts — Schreiben liefert 403 premium_required. */
  guard_available?: boolean | null
  notify_poc?: boolean | null
  /** Ergebnis des v2-Guard-Evaluators — bewusst getrennt vom Schalter. */
  guard_status?: 'ok' | 'warning' | 'critical' | null
  failed_guards?: string[] | null

  // --- Hero-Status (Welle 3 / FE-Story 1.6, additiv im Detail-Response) ---
  /** CDR-Verifikation (HIP-131/133). */
  cdr_verification?: HotspotHeroStatus['cdr_verification']
  /** Ban-Status (BanAlertService + Denylist). */
  ban_status?: HotspotHeroStatus['ban_status']
  /** Unique-Connections-Meter (HIP-134, Schwelle 25). */
  unique_connections?: HotspotHeroStatus['unique_connections']

  /** F-036/suite#3: Heartbeat-Verlauf, bereits im Show-Response enthalten. */
  heartbeats?: HotspotHeartbeat[]

  /**
   * Issue web#97: true wenn der Hotspot im Bestand des aktuellen Users liegt.
   * Die Detailseite ist seit web#97 fuer JEDEN Hotspot lesbar (v1-Paritaet) —
   * besitzerabhaengige Felder (Guard-Einstellungen, heartbeats) sind bei
   * false bereits vom BE nil/leer, is_owner steuert zusaetzlich das FE-UI
   * (Notizen/Tags/Splits/Bearbeiten/Entfernen nur fuer den Besitzer).
   */
  is_owner?: boolean

  /**
   * Issue #88: Netzanteil dieses Hotspots an der letzten vollstaendig
   * berechneten IoT-Reward-Epoche. Additiv, nicht besitzerexklusiv (nutzt
   * dieselben oeffentlichen Reward-Zahlen wie hnt_24h/hnt_30d). null fuer
   * Mobile-Hotspots (Phase 1 deckt nur IoT ab) oder solange kein Manifest
   * importiert wurde.
   */
  network_share?: NetworkShare | null
}

type HotspotDetailResponse = {
  data: HotspotDetail
}

type UseHotspotDetailResult = {
  data: HotspotDetail | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

// --- Hook ---

export function useHotspotDetail(id: string): UseHotspotDetailResult {
  const query = useApi<HotspotDetailResponse>(
    ['hotspot', id],
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.GET('/api/v2/hotspots/{id}', {
        params: { path: { id } },
      })
      // BUGFIX (onboarding-wallet-hotspot #9a): Das Backend liefert rewards_24h/
      // rewards_7d/rewards_30d, nicht hnt_24h/hnt_7d/hnt_30d. useHotspots.ts (Liste)
      // normalisiert das bereits — hier fehlte dieselbe Normalisierung, wodurch die
      // Kopfzeile (HotspotHeroBar) immer "—" zeigte, obwohl Rewards-Daten vorhanden
      // waren (sichtbar in der Rewards-Aufschluesselung darunter, die eine andere
      // Datenquelle nutzt).
      // result.data ist die Response-Envelope { data: HotspotDetail } — das
      // eigentliche Hotspot-Objekt liegt eine Ebene tiefer unter .data.data.
      const envelope = result?.data as { data?: Record<string, unknown> } | undefined
      const raw = envelope?.data
      if (!raw) return result as { data?: HotspotDetailResponse; error?: unknown }

      const normalized = {
        ...raw,
        hnt_24h: (raw.hnt_24h ?? raw.rewards_24h ?? null) as number | null,
        hnt_7d: (raw.hnt_7d ?? raw.rewards_7d ?? null) as number | null,
        hnt_30d: (raw.hnt_30d ?? raw.rewards_30d ?? null) as number | null,
      }

      return { ...result, data: { ...envelope, data: normalized } } as { data?: HotspotDetailResponse; error?: unknown }
    },
  )

  return {
    data: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
